import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Group,
  Text,
  Stack,
  TextInput,
  Select,
  MultiSelect,
  Chip,
  Divider,
  useMantineTheme,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconFilter, IconCalendar, IconTag, IconUser, IconFolder } from '@tabler/icons-react';
import { Modal, Button } from '../../../ui';
import { useDashboardStore, selectFiltersModalWidget } from '../../../../stores/dashboardStore';
import { getWidget } from '../registry';
import { useDashboardContext } from '../context/DashboardProvider';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// Filter Types
// ============================================================================

interface FilterConfig {
  key: string;
  label: string;
  type: 'text' | 'select' | 'multiselect' | 'date' | 'daterange' | 'chips';
  options?: { value: string; label: string }[];
  placeholder?: string;
  icon?: React.ReactNode;
}

// ============================================================================
// WidgetFiltersModal Component
// ============================================================================

export interface WidgetFiltersModalProps {
  /** Whether the modal is open */
  opened: boolean;
  /** Callback to close the modal */
  onClose: () => void;
}

export function WidgetFiltersModal({ opened, onClose }: WidgetFiltersModalProps) {
  const theme = useMantineTheme();
  const filtersModalWidget = useDashboardStore(selectFiltersModalWidget);
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };
  const widgets = useDashboardStore((state) => state.widgets);
  const setWidgetFilters = useDashboardStore((state) => state.setWidgetFilters);
  const closeFiltersModal = useDashboardStore((state) => state.closeFiltersModal);

  // Get dashboard data for filter options
  const { data: globalData } = useDashboardContext();

  // Get the widget instance and definition
  const widgetInstance = useMemo(() => {
    if (!filtersModalWidget) return null;
    return widgets.find((w) => w.instanceId === filtersModalWidget);
  }, [filtersModalWidget, widgets]);

  const widgetDefinition = useMemo(() => {
    if (!widgetInstance) return null;
    return getWidget(widgetInstance.widgetId);
  }, [widgetInstance]);

  // Local state for filters
  const [localFilters, setLocalFilters] = useState<Record<string, unknown>>({});

  // Initialize local state when modal opens
  useEffect(() => {
    if (widgetInstance) {
      setLocalFilters(widgetInstance.filters || {});
    }
  }, [widgetInstance]);

  // Generate filter configurations based on widget type
  const filterConfigs = useMemo<FilterConfig[]>(() => {
    if (!widgetDefinition) return [];

    // Get unique values from global data for filter options
    const projectOptions = globalData?.projects?.map((p) => ({
      value: p.id,
      label: p.name,
    })) || [];

    const userOptions = globalData?.users?.map((u) => ({
      value: u.id,
      label: u.name,
    })) || [];

    const statusOptions = [
      { value: 'all', label: 'All' },
      { value: 'active', label: 'Active' },
      { value: 'pending', label: 'Pending' },
      { value: 'completed', label: 'Completed' },
    ];

    const priorityOptions = [
      { value: 'all', label: 'All' },
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'urgent', label: 'Urgent' },
    ];

    const categoryFilters: FilterConfig[] = [];

    // Add common filters based on widget category
    switch (widgetDefinition.category) {
      case 'tasks':
        categoryFilters.push(
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'all', label: 'All' },
              { value: 'todo', label: 'To Do' },
              { value: 'in-progress', label: 'In Progress' },
              { value: 'review', label: 'Review' },
              { value: 'done', label: 'Done' },
              { value: 'blocked', label: 'Blocked' },
            ],
            icon: <IconTag size={14} />,
          },
          {
            key: 'priority',
            label: 'Priority',
            type: 'chips',
            options: priorityOptions.slice(1), // Remove 'all'
          },
          {
            key: 'assignee',
            label: 'Assignee',
            type: 'multiselect',
            options: userOptions,
            icon: <IconUser size={14} />,
          }
        );
        break;

      case 'projects':
        categoryFilters.push(
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'all', label: 'All' },
              { value: 'planning', label: 'Planning' },
              { value: 'in-progress', label: 'In Progress' },
              { value: 'on-hold', label: 'On Hold' },
              { value: 'completed', label: 'Completed' },
            ],
            icon: <IconFolder size={14} />,
          },
          {
            key: 'priority',
            label: 'Priority',
            type: 'chips',
            options: priorityOptions.slice(1),
          }
        );
        break;

      case 'stats':
        categoryFilters.push(
          {
            key: 'dateRange',
            label: 'Date Range',
            type: 'daterange',
            icon: <IconCalendar size={14} />,
          }
        );
        break;

      case 'lists':
        categoryFilters.push(
          {
            key: 'search',
            label: 'Search',
            type: 'text',
            placeholder: 'Filter items...',
          },
          {
            key: 'limit',
            label: 'Show Items',
            type: 'select',
            options: [
              { value: '5', label: '5 items' },
              { value: '10', label: '10 items' },
              { value: '15', label: '15 items' },
              { value: '20', label: '20 items' },
            ],
          }
        );
        break;

      case 'people':
        categoryFilters.push(
          {
            key: 'department',
            label: 'Department',
            type: 'multiselect',
            options: [
              { value: 'engineering', label: 'Engineering' },
              { value: 'design', label: 'Design' },
              { value: 'marketing', label: 'Marketing' },
              { value: 'sales', label: 'Sales' },
              { value: 'hr', label: 'HR' },
            ],
          },
          {
            key: 'status',
            label: 'Status',
            type: 'chips',
            options: [
              { value: 'active', label: 'Active' },
              { value: 'away', label: 'Away' },
              { value: 'busy', label: 'Busy' },
              { value: 'offline', label: 'Offline' },
            ],
          }
        );
        break;

      default:
        // Generic filters
        categoryFilters.push(
          {
            key: 'search',
            label: 'Search',
            type: 'text',
            placeholder: 'Search...',
          }
        );
    }

    return categoryFilters;
  }, [widgetDefinition, globalData]);

  // Handle save
  const handleApply = () => {
    if (filtersModalWidget) {
      setWidgetFilters(filtersModalWidget, localFilters);
    }
    handleClose();
  };

  // Handle clear
  const handleClear = () => {
    setLocalFilters({});
  };

  // Handle close
  const handleClose = () => {
    closeFiltersModal();
    onClose();
  };

  // Update local filter
  const updateFilter = (key: string, value: unknown) => {
    setLocalFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Render filter input based on type
  const renderFilterInput = (config: FilterConfig) => {
    const value = localFilters[config.key];

    switch (config.type) {
      case 'text':
        return (
          <TextInput
            label={config.label}
            placeholder={config.placeholder}
            value={(value as string) || ''}
            onChange={(e) => updateFilter(config.key, e.currentTarget.value)}
            leftSection={config.icon}
          />
        );

      case 'select':
        return (
          <Select
            label={config.label}
            placeholder={`Select ${config.label.toLowerCase()}`}
            value={(value as string) || null}
            onChange={(val) => updateFilter(config.key, val)}
            data={config.options || []}
            leftSection={config.icon}
            clearable
          />
        );

      case 'multiselect':
        return (
          <MultiSelect
            label={config.label}
            placeholder={`Select ${config.label.toLowerCase()}`}
            value={(value as string[]) || []}
            onChange={(val) => updateFilter(config.key, val)}
            data={config.options || []}
            leftSection={config.icon}
            clearable
            searchable
          />
        );

      case 'chips':
        return (
          <Box>
            <Text size="sm" fw={500} mb="xs" style={accessibilityStyles}>
              {config.label}
            </Text>
            <Chip.Group
              multiple
              value={(value as string[]) || []}
              onChange={(val) => updateFilter(config.key, val)}
            >
              <Group gap="xs">
                {config.options?.map((opt) => (
                  <Chip key={opt.value} value={opt.value} size="sm">
                    {opt.label}
                  </Chip>
                ))}
              </Group>
            </Chip.Group>
          </Box>
        );

      case 'daterange':
        return (
          <DatePickerInput
            type="range"
            label={config.label}
            placeholder="Select date range"
            value={(value as [Date | null, Date | null]) || [null, null]}
            onChange={(val) => updateFilter(config.key, val)}
            leftSection={config.icon}
            clearable
          />
        );

      default:
        return null;
    }
  };

  if (!widgetInstance || !widgetDefinition) {
    return null;
  }

  const Icon = widgetDefinition.icon;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconFilter size={20} />
          <Text fw={600} style={accessibilityStyles}>Filter Widget</Text>
        </Group>
      }
      size="md"
    >
      <Stack gap="md">
        {/* Widget Info */}
        <Box
          p="sm"
          style={{
            backgroundColor: theme.colors.gray[0],
            borderRadius: theme.radius.md,
          }}
        >
          <Group gap="sm">
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: theme.radius.md,
                backgroundColor: theme.colors.blue[1],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon size={20} />
            </Box>
            <Box>
              <Text fw={600} size="sm" style={accessibilityStyles}>
                {widgetDefinition.name}
              </Text>
              <Text size="xs" c="dimmed" style={accessibilityStyles}>
                Configure filters to customize displayed data
              </Text>
            </Box>
          </Group>
        </Box>

        <Divider />

        {/* Filter Inputs */}
        {filterConfigs.length > 0 ? (
          <Stack gap="md">
            {filterConfigs.map((config) => (
              <Box key={config.key}>{renderFilterInput(config)}</Box>
            ))}
          </Stack>
        ) : (
          <Text c="dimmed" ta="center" py="lg" style={accessibilityStyles}>
            No filters available for this widget.
          </Text>
        )}

        <Divider />

        {/* Actions */}
        <Group justify="space-between">
          <Button variant="subtle" color="gray" onClick={handleClear}>
            Clear All
          </Button>
          <Group gap="sm">
            <Button variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleApply}>Apply Filters</Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

export default WidgetFiltersModal;
